import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Coins, MessageCircle, Phone, ShoppingCart, User, Users } from 'lucide-react';
import { fundingAuthAPI, getIpeWorkspaceMode } from '@/utils/api';
import { useIpeAuth } from '@/context/IpeAuthContext';
import {
  addRechargeCartItem,
  buildRechargeCartLineKey,
  removeRechargeCartItems,
} from '@/utils/ipeRechargeCart';
import RechargeCartCheckoutModal from '@/components/ipe/RechargeCartCheckoutModal';
import RechargeScanPayPlaceholder from '@/components/ipe/RechargeScanPayPlaceholder';
import { isScanPayPlaceholderVisible, resolvePayMethodHint } from '@/constants/onlinePaymentFreeze';
import DurationPicker from '@/pages/components/DurationPicker';
import UserAvatar from './UserAvatar';
import { buildCreditConsumptionUrl } from '@/config/ipeRoutes';
import {
  MEMBERSHIP_DURATION_OPTIONS,
  calcMembershipOriginalTotal,
  calcMembershipTotalPrice,
  calcShopExpandTotalPrice,
  formatDurationPrice,
  formatDurationShortLabel,
  formatShopDurationPrice,
  formatYuanAmount,
} from '@/utils/membershipDuration';
import {
  PLAN_LABELS,
  formatCredits,
  formatStorageMb,
  maskPhone,
} from '@/pages/ipe/workbench/utils/accountCenterUtils';
import '@/styles/ipe/ipe-credits-purchase-modal.css';

const MEMBERSHIP_SIDEBAR = {
  title: 'IPE 会员',
  subtitle: '解锁全站 AI 与自动化能力',
  sections: [
    {
      title: '会员专属',
      items: [
        'AI 万能改图与智能抠图',
        '批量上架与模板导出',
        '侵权检测与标题提取',
        '工作流自动化任务',
      ],
    },
    {
      title: '基础权益',
      items: [
        '档位店铺授权',
        '每月赠送 IPE 豆',
        '团队共享额度',
        '图片空间容量',
      ],
    },
  ],
  cta: '查看权益对比',
};

const PACKS_SIDEBAR = {
  title: '服务增强',
  subtitle: '按需充值，灵活扩容',
  sections: [
    {
      title: 'IPE 豆充值',
      items: [
        '多档位阶梯，买多更省',
        '一次充值，长期可用',
        '个人 / 团队分轨独立',
        '与会员赠送豆叠加',
      ],
    },
    {
      title: '扩容服务',
      items: [
        '店铺授权一次性扩容',
        '图片空间按时长订阅',
        '与会员档位权益叠加',
        '长时长享更多折扣',
      ],
    },
  ],
  cta: 'IPE 豆消耗说明',
};

const MEMBERSHIP_SCOPE_TABS = [
  { key: 'personal', label: '个人会员', icon: User },
  { key: 'team', label: '团队会员', icon: Users },
];

function AsideDecoMembership() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="100" cy="108" r="72" stroke="currentColor" strokeWidth="1.5" opacity="0.18" />
      <circle cx="100" cy="108" r="52" stroke="currentColor" strokeWidth="1.5" opacity="0.12" />
      <path
        d="M60 118 L100 58 L140 118 L125 118 L125 148 L75 148 L75 118 Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
        opacity="0.22"
      />
      <path
        d="M82 118 L100 88 L118 118"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.16"
      />
    </svg>
  );
}

function AsideDecoPacks() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="118" cy="82" r="34" stroke="currentColor" strokeWidth="2" opacity="0.14" />
      <circle cx="88" cy="108" r="38" stroke="currentColor" strokeWidth="2" opacity="0.18" />
      <circle cx="112" cy="132" r="42" stroke="currentColor" strokeWidth="2" opacity="0.22" />
      <ellipse cx="112" cy="132" rx="42" ry="10" stroke="currentColor" strokeWidth="1.5" opacity="0.1" />
      <path
        d="M96 118 C96 110 104 104 112 104 C120 104 128 110 128 118"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.16"
      />
    </svg>
  );
}

const FREE_TIER_CODES = new Set(['free', 'trial']);

function isPurchasableMembershipTier(row) {
  if (!row) return false;
  if (FREE_TIER_CODES.has(String(row.tier || '').toLowerCase())) return false;
  if (String(row.label || '').includes('免费')) return false;
  const price = Number(row.priceYuan);
  if (!Number.isNaN(price) && price <= 0) return false;
  return true;
}

const PACK_GROUP_LABELS = {
  credit: 'IPE 豆充值',
  shop: '店铺授权扩容',
  storage: '图片空间扩容',
};

/** 仅图片空间扩容按时长计费；IPE 豆充值与店铺扩容为一次性购买 */
const PACK_GROUPS_WITH_DURATION = new Set(['storage']);

function packGroupHasDuration(group) {
  return PACK_GROUPS_WITH_DURATION.has(group);
}

function resolvePackUnitPrice(pack) {
  return Number(pack?.monthlyPriceYuan) || 0;
}

function formatOneTimePackPrice(pack) {
  const price = resolvePackUnitPrice(pack);
  if (!price) return null;
  return `¥${formatYuanAmount(price)}`;
}

function calcPackTotalPrice(monthlyPrice, months, isShop) {
  const price = Number(monthlyPrice) || 0;
  if (!price) return 0;
  return isShop
    ? calcShopExpandTotalPrice(price, months)
    : calcMembershipTotalPrice(price, months);
}

function formatPackAmount(pack, group) {
  if (group === 'credit') return `${formatCredits(pack.amount)} IPE 豆`;
  if (group === 'shop') return `+${pack.amount} 家店铺`;
  return formatStorageMb(pack.amount);
}

function resolveDisplayName(user) {
  return (user?.nickname && String(user.nickname).trim()) || maskPhone(user?.phone);
}

function resolveScopedQuota(center, packScope) {
  if (!center) return {};
  if (packScope === 'team' && center.teamQuota) return center.teamQuota;
  return center.personalQuota || center.quota || {};
}

function resolveScopedPlanTier(user, scopedQuota, packScope) {
  if (scopedQuota?.planTier) {
    return PLAN_LABELS[scopedQuota.planTier] || scopedQuota.planTierLabel || scopedQuota.planTier;
  }
  if (packScope === 'team') {
    const tier = user?.teamPlanTier;
    return tier && tier !== 'free' ? (PLAN_LABELS[tier] || tier) : '免费会员';
  }
  const tier = user?.personalPlanTier || user?.planTier;
  return PLAN_LABELS[tier] || tier || '免费会员';
}

function tierMatchesScope(tierCode, scope) {
  const code = String(tierCode || '');
  if (scope === 'team') return code.startsWith('team_');
  return code.startsWith('personal_') || (!code.startsWith('team_') && !code.startsWith('personal_'));
}

function CardsCarousel({ children, depKey = '' }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(() => updateScrollState());
    observer.observe(el);
    return () => observer.disconnect();
  }, [children, depKey, updateScrollState]);

  const scrollBy = useCallback((direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector('.ipe-credits-purchase-modal__plan-card');
    const step = ((card?.offsetWidth || 168) + 10) * 2;
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
    window.setTimeout(updateScrollState, 300);
  }, [updateScrollState]);

  const showNav = canScrollLeft || canScrollRight;

  return (
    <div className="ipe-credits-purchase-modal__cards-carousel">
      {showNav ? (
        <button
          type="button"
          className="ipe-credits-purchase-modal__cards-nav ipe-credits-purchase-modal__cards-nav--prev"
          aria-label="向左查看档位"
          disabled={!canScrollLeft}
          onClick={() => scrollBy(-1)}
        >
          <ChevronLeft size={18} strokeWidth={2.2} aria-hidden="true" />
        </button>
      ) : null}
      <div
        ref={scrollRef}
        className="ipe-credits-purchase-modal__cards-scroll ipe-credits-purchase-modal__cards-scroll--in-panel"
        onScroll={updateScrollState}
      >
        {children}
      </div>
      {showNav ? (
        <button
          type="button"
          className="ipe-credits-purchase-modal__cards-nav ipe-credits-purchase-modal__cards-nav--next"
          aria-label="向右查看档位"
          disabled={!canScrollRight}
          onClick={() => scrollBy(1)}
        >
          <ChevronRight size={18} strokeWidth={2.2} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

function parseRechargeSearchParams(searchParams) {
  const tab = searchParams.get('tab') === 'packs' ? 'packs' : 'membership';
  const scopeRaw = searchParams.get('scope');
  const packScope = scopeRaw === 'team' || scopeRaw === 'personal' ? scopeRaw : null;
  const groupRaw = searchParams.get('group');
  const packGroup = tab === 'packs' && ['credit', 'shop', 'storage'].includes(groupRaw) ? groupRaw : null;
  return { tab, packScope, packGroup };
}

export default function IpeCreditsPurchaseView({
  showToast,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const skipUrlApplyRef = useRef(false);
  const urlBootRef = useRef(parseRechargeSearchParams(searchParams));
  const urlBoot = urlBootRef.current;
  const [loading, setLoading] = useState(false);
  const [center, setCenter] = useState(null);
  const [activeTab, setActiveTab] = useState(urlBoot.tab === 'packs' ? 'packs' : 'membership');
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedPackKey, setSelectedPackKey] = useState(null);
  const [selectedDurationMonths, setSelectedDurationMonths] = useState(1);
  const [rechargeDurationMonths, setRechargeDurationMonths] = useState(1);
  const [activePackGroup, setActivePackGroup] = useState(
    urlBoot.packGroup && ['credit', 'shop', 'storage'].includes(urlBoot.packGroup)
      ? urlBoot.packGroup
      : 'credit',
  );
  const [packScope, setPackScope] = useState(() => (
    urlBoot.packScope === 'team' || urlBoot.packScope === 'personal'
      ? urlBoot.packScope
      : getIpeWorkspaceMode()
  ));
  const { user: authUser } = useIpeAuth();
  const cartUserId = authUser?.id ?? authUser?._id;
  const [intentCheckoutOpen, setIntentCheckoutOpen] = useState(false);
  const [intentCheckoutItems, setIntentCheckoutItems] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fundingAuthAPI.getAccountCenter();
      setCenter(data);
    } catch (e) {
      showToast?.(e?.message || '加载账号信息失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const writeSearchParams = useCallback((tab, scope, packGroup) => {
    const params = new URLSearchParams();
    params.set('tab', tab === 'packs' ? 'packs' : 'membership');
    if (scope === 'team' || scope === 'personal') {
      params.set('scope', scope);
    }
    if (tab === 'packs' && packGroup && ['credit', 'shop', 'storage'].includes(packGroup)) {
      params.set('group', packGroup);
    }
    const next = params.toString();
    if (next === searchParams.toString()) return;
    skipUrlApplyRef.current = true;
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const applyUrlState = useCallback((nextParams) => {
    const { tab, packScope: scope, packGroup } = parseRechargeSearchParams(nextParams);
    setActiveTab(tab === 'packs' ? 'packs' : 'membership');
    setSelectedPackKey(null);
    setPackScope(scope === 'team' || scope === 'personal' ? scope : getIpeWorkspaceMode());
    if (tab === 'packs' && packGroup) {
      setActivePackGroup(packGroup);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (skipUrlApplyRef.current) {
      skipUrlApplyRef.current = false;
      return;
    }
    applyUrlState(searchParams);
  }, [applyUrlState, searchParams]);

  useEffect(() => {
    const syncScope = (ev) => {
      const next = ev?.detail?.mode;
      if (next === 'personal' || next === 'team') {
        setPackScope(next);
        load();
      }
    };
    window.addEventListener('ipe:workspace-mode-changed', syncScope);
    return () => window.removeEventListener('ipe:workspace-mode-changed', syncScope);
  }, [load]);

  useEffect(() => {
    writeSearchParams(activeTab, packScope, activeTab === 'packs' ? activePackGroup : null);
  }, [activeTab, packScope, activePackGroup, writeSearchParams]);

  const teamWorkspaceAvailable = Boolean(center?.teamWorkspaceAvailable);

  useEffect(() => {
    if (urlBoot.packScope === 'team') return;
    // 开通会员页允许选择团队会员（用于新购）；服务增强页仍按团队工作区可用性限制
    if (activeTab === 'membership') return;
    if (!teamWorkspaceAvailable && packScope === 'team') {
      setPackScope('personal');
    }
  }, [teamWorkspaceAvailable, packScope, urlBoot.packScope, activeTab]);

  const scopedQuota = useMemo(
    () => resolveScopedQuota(center, packScope),
    [center, packScope],
  );
  const user = center?.user || {};
  const recharge = center?.recharge || {};
  const displayName = resolveDisplayName(user);
  const planLabel = resolveScopedPlanTier(user, scopedQuota, packScope);
  const shopLimit = Number(scopedQuota.shopLimit) || 0;
  const connectedShops = Number(scopedQuota.connectedShops) || 0;
  const creditBalance = Number(scopedQuota.creditBalance ?? scopedQuota.usageRemaining) || 0;
  const creditsEmpty = scopedQuota.creditsEmpty || creditBalance <= 0;
  const membershipTiers = center?.membershipTiers?.length ? center.membershipTiers : center?.usageTiers || [];
  const scopedMembershipTiers = useMemo(
    () => membershipTiers.filter((row) => tierMatchesScope(row.tier, packScope)),
    [membershipTiers, packScope],
  );
  const purchasableMembershipTiers = useMemo(
    () => scopedMembershipTiers.filter(isPurchasableMembershipTier),
    [scopedMembershipTiers],
  );
  const creditPacks = center?.creditRechargePacks || [];
  const shopPacks = center?.shopRechargePacks || [];
  const storagePacks = center?.storageRechargePacks || [];
  const availablePackGroups = useMemo(() => {
    const groups = [];
    if (creditPacks.length) groups.push('credit');
    if (shopPacks.length) groups.push('shop');
    if (storagePacks.length) groups.push('storage');
    return groups;
  }, [creditPacks, shopPacks, storagePacks]);
  const activePackList = useMemo(() => {
    if (activePackGroup === 'credit') return creditPacks;
    if (activePackGroup === 'shop') return shopPacks;
    return storagePacks;
  }, [activePackGroup, creditPacks, shopPacks, storagePacks]);
  const durationOptions = center?.membershipDurationOptions?.length
    ? center.membershipDurationOptions.map((o) => ({
        code: o.code,
        label: o.label,
        months: Number(o.months),
        payPercent: Number(o.payPercent),
        discountLabel: o.discountLabel,
      }))
    : MEMBERSHIP_DURATION_OPTIONS;
  const contactHint = recharge.hint || scopedQuota.upgradeContactHint || '请联系商务充值';

  useEffect(() => {
    if (!purchasableMembershipTiers.length) return;
    const tierField = packScope === 'team' ? user.teamPlanTier : (user.personalPlanTier || user.planTier);
    const current = purchasableMembershipTiers.find((t) => t.tier === tierField);
    setSelectedTier(current?.tier || purchasableMembershipTiers[0]?.tier || null);
  }, [purchasableMembershipTiers, user.planTier, user.personalPlanTier, user.teamPlanTier, packScope]);

  useEffect(() => {
    if (!availablePackGroups.length) return;
    if (!availablePackGroups.includes(activePackGroup)) {
      setActivePackGroup(availablePackGroups[0]);
    }
  }, [availablePackGroups, activePackGroup]);

  useEffect(() => {
    if (activeTab !== 'packs' || !activePackList.length) return;
    const currentGroup = selectedPackKey?.split(':')[0];
    if (!selectedPackKey || currentGroup !== activePackGroup) {
      setSelectedPackKey(`${activePackGroup}:${activePackList[0].id}`);
    }
  }, [activeTab, activePackGroup, activePackList, selectedPackKey]);

  const selectedTierRow = useMemo(
    () => purchasableMembershipTiers.find((t) => t.tier === selectedTier) || purchasableMembershipTiers[0] || null,
    [purchasableMembershipTiers, selectedTier],
  );

  const selectedPack = useMemo(() => {
    if (!selectedPackKey) return null;
    const [group, id] = selectedPackKey.split(':');
    const list = group === 'credit' ? creditPacks : group === 'shop' ? shopPacks : storagePacks;
    return list.find((p) => String(p.id) === id) || null;
  }, [selectedPackKey, creditPacks, shopPacks, storagePacks]);

  const selectedPackGroup = selectedPackKey?.split(':')[0] || activePackGroup;
  const activePackHasDuration = packGroupHasDuration(activePackGroup);
  const selectedPackPriceLabel = useMemo(() => {
    if (!selectedPack?.monthlyPriceYuan) return null;
    const price = resolvePackUnitPrice(selectedPack);
    if (!packGroupHasDuration(selectedPackGroup)) {
      return formatOneTimePackPrice(selectedPack);
    }
    if (selectedPackGroup === 'shop') {
      return formatShopDurationPrice(price, rechargeDurationMonths);
    }
    return formatDurationPrice(price, rechargeDurationMonths);
  }, [selectedPack, selectedPackGroup, rechargeDurationMonths]);

  const payPriceLabel = useMemo(() => {
    if (activeTab === 'membership' && selectedTierRow?.priceYuan && Number(selectedTierRow.priceYuan) > 0) {
      return formatDurationPrice(Number(selectedTierRow.priceYuan), selectedDurationMonths);
    }
    if (activeTab === 'packs' && selectedPack?.monthlyPriceYuan) {
      const price = resolvePackUnitPrice(selectedPack);
      const group = selectedPackKey?.split(':')[0] || activePackGroup;
      if (!packGroupHasDuration(group)) {
        return formatOneTimePackPrice(selectedPack) || '联系商务报价';
      }
      if (group === 'shop') {
        return formatShopDurationPrice(price, rechargeDurationMonths);
      }
      return formatDurationPrice(price, rechargeDurationMonths);
    }
    return '联系商务报价';
  }, [
    activeTab,
    activePackGroup,
    selectedTierRow,
    selectedDurationMonths,
    selectedPack,
    selectedPackKey,
    rechargeDurationMonths,
  ]);

  const buildSelectedCartItem = useCallback(() => {
    if (activeTab === 'membership') {
      if (!selectedTierRow) {
        return { error: '请先选择会员档位' };
      }
      const monthlyPrice = Number(selectedTierRow.priceYuan) || 0;
      if (monthlyPrice <= 0) {
        return { error: '当前档位暂不支持下单，请联系商务' };
      }
      const priceYuan = calcMembershipTotalPrice(monthlyPrice, selectedDurationMonths);
      return {
        item: {
          kind: 'membership',
          scope: packScope,
          tier: selectedTierRow.tier,
          title: selectedTierRow.label,
          summary: formatDurationShortLabel(selectedDurationMonths),
          unitPriceYuan: priceYuan,
          priceYuan,
          priceLabel: formatDurationPrice(monthlyPrice, selectedDurationMonths),
          durationMonths: selectedDurationMonths,
          productTypeLabel: '会员开通/续费',
        },
      };
    }
    if (!selectedPack) {
      return { error: '请先选择充值套餐' };
    }
    const group = selectedPackKey?.split(':')[0] || activePackGroup;
    const monthlyPrice = resolvePackUnitPrice(selectedPack);
    if (monthlyPrice <= 0) {
      return { error: '当前套餐暂不支持下单，请联系商务' };
    }
    const hasDuration = packGroupHasDuration(group);
    const priceYuan = hasDuration
      ? calcPackTotalPrice(monthlyPrice, rechargeDurationMonths, group === 'shop')
      : monthlyPrice;
    const durationMonths = hasDuration ? rechargeDurationMonths : 1;
    return {
      item: {
        kind: 'pack',
        scope: packScope,
        packGroup: group,
        packId: selectedPack.id,
        title: selectedPack.label,
        summary: hasDuration
          ? `${PACK_GROUP_LABELS[group] || group} · ${formatDurationShortLabel(durationMonths)}`
          : `${PACK_GROUP_LABELS[group] || group} · ${formatPackAmount(selectedPack, group)}`,
        unitPriceYuan: priceYuan,
        priceYuan,
        priceLabel: payPriceLabel,
        durationMonths,
        productTypeLabel: PACK_GROUP_LABELS[group] || '充值套餐',
      },
    };
  }, [
    activeTab,
    activePackGroup,
    packScope,
    payPriceLabel,
    rechargeDurationMonths,
    selectedDurationMonths,
    selectedPack,
    selectedPackKey,
    selectedTierRow,
  ]);

  const handleAddToCart = useCallback(() => {
    const result = buildSelectedCartItem();
    if (result.error) {
      showToast?.(result.error, 'error');
      return;
    }
    addRechargeCartItem(result.item, cartUserId);
    showToast?.('已加入购物车', 'success');
  }, [buildSelectedCartItem, cartUserId, showToast]);

  const handleOpenIntentCheckout = useCallback(() => {
    const result = buildSelectedCartItem();
    if (result.error) {
      showToast?.(result.error, 'error');
      return;
    }
    const item = {
      ...result.item,
      id: buildRechargeCartLineKey(result.item),
    };
    setIntentCheckoutItems([item]);
    setIntentCheckoutOpen(true);
  }, [buildSelectedCartItem, showToast]);

  const sidebarContent = activeTab === 'packs' ? PACKS_SIDEBAR : MEMBERSHIP_SIDEBAR;

  const handleAsideCta = () => {
    const url = activeTab === 'membership'
      ? buildCreditConsumptionUrl({ tab: 'membership' })
      : buildCreditConsumptionUrl({ scroll: 'consumption' });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="ipe-credits-purchase-modal__shell ipe-credits-purchase-view__shell"
      aria-labelledby="ipe-credits-purchase-title"
    >
      <RechargeCartCheckoutModal
        open={intentCheckoutOpen}
        onClose={() => setIntentCheckoutOpen(false)}
        items={intentCheckoutItems}
        showToast={showToast}
        onSubmitted={(submitted) => {
          removeRechargeCartItems(
            submitted.map((item) => item.id).filter(Boolean),
            cartUserId,
          );
          setIntentCheckoutOpen(false);
          setIntentCheckoutItems([]);
        }}
      />
        <aside
          className={`ipe-credits-purchase-modal__aside ipe-credits-purchase-modal__aside--${activeTab}`}
        >
          <div className="ipe-credits-purchase-modal__aside-body">
            <h2 id="ipe-credits-purchase-title" className="ipe-credits-purchase-modal__aside-title">
              {sidebarContent.title}
            </h2>
            <p className="ipe-credits-purchase-modal__aside-subtitle">{sidebarContent.subtitle}</p>
            {sidebarContent.sections.map((section) => (
              <div key={section.title} className="ipe-credits-purchase-modal__aside-section">
                <h3 className="ipe-credits-purchase-modal__aside-section-title">{section.title}</h3>
                <ul className="ipe-credits-purchase-modal__aside-list">
                  {section.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
            <button
              type="button"
              className="ipe-credits-purchase-modal__aside-compare"
              onClick={handleAsideCta}
            >
              {sidebarContent.cta}
              <ChevronRight size={14} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
          <div className="ipe-credits-purchase-modal__aside-deco" aria-hidden="true">
            {activeTab === 'packs' ? <AsideDecoPacks /> : <AsideDecoMembership />}
          </div>
        </aside>

        {loading && !center ? (
          <div className="ipe-credits-purchase-modal__loading">
            <div className="ipe-credits-purchase-modal__skeleton" />
            <div className="ipe-credits-purchase-modal__skeleton ipe-credits-purchase-modal__skeleton--short" />
          </div>
        ) : (
          <div className="ipe-credits-purchase-modal__main">
            <header className="ipe-credits-purchase-modal__user-bar">
              <div className="ipe-credits-purchase-modal__user-info">
                <UserAvatar avatarUrl={user.avatarUrl} displayName={displayName} size={40} />
                <div className="ipe-credits-purchase-modal__user-text">
                  <div className="ipe-credits-purchase-modal__user-phone">{maskPhone(user.phone)}</div>
                  <div className="ipe-credits-purchase-modal__user-plan">
                    {packScope === 'team' ? '团队档位' : '个人档位'}：{planLabel}
                  </div>
                </div>
              </div>
              <div className="ipe-credits-purchase-modal__user-actions">
                {activeTab === 'packs' && (teamWorkspaceAvailable || urlBoot.packScope === 'team') ? (
                  <div className="ipe-credits-purchase-modal__scope-switch" role="group" aria-label="充值归属">
                    <button
                      type="button"
                      className={`ipe-credits-purchase-modal__scope-btn${packScope === 'personal' ? ' is-active' : ''}`}
                      onClick={() => setPackScope('personal')}
                      aria-pressed={packScope === 'personal'}
                    >
                      <User size={14} strokeWidth={2} aria-hidden="true" />
                      个人充值
                    </button>
                    <button
                      type="button"
                      className={`ipe-credits-purchase-modal__scope-btn${packScope === 'team' ? ' is-active' : ''}`}
                      onClick={() => setPackScope('team')}
                      aria-pressed={packScope === 'team'}
                    >
                      <Users size={14} strokeWidth={2} aria-hidden="true" />
                      团队充值
                    </button>
                  </div>
                ) : null}
                <div className="ipe-credits-purchase-modal__balance-chip">
                  <Coins size={15} strokeWidth={2} aria-hidden="true" />
                  {packScope === 'team' ? '团队IPE豆' : '个人IPE豆'}
                  <strong>{formatCredits(creditBalance)}</strong>
                </div>
              </div>
            </header>

            <div className="ipe-credits-purchase-modal__tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'membership'}
                className={`ipe-credits-purchase-modal__tab${activeTab === 'membership' ? ' ipe-credits-purchase-modal__tab--active' : ''}`}
                onClick={() => setActiveTab('membership')}
              >
                开通会员
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'packs'}
                className={`ipe-credits-purchase-modal__tab${activeTab === 'packs' ? ' ipe-credits-purchase-modal__tab--active' : ''}`}
                onClick={() => setActiveTab('packs')}
              >
                服务增强
              </button>
            </div>

            <div className="ipe-credits-purchase-modal__content">
              {(scopedQuota.shopAtLimit || creditsEmpty) && (
                <div className="ipe-credits-purchase-modal__alert" role="alert">
                  {scopedQuota.shopAtLimit && (
                    <p>
                      当前{packScope === 'team' ? '团队' : '个人'}店铺授权已达上限（{connectedShops}/{shopLimit}），请充值店铺扩容。
                    </p>
                  )}
                  {creditsEmpty && <p>{packScope === 'team' ? '团队' : '个人'}IPE豆余额不足，部分 AI 功能将无法使用。</p>}
                  <p>{contactHint}</p>
                </div>
              )}

              {activeTab === 'membership' ? (
                <div className="ipe-credits-purchase-modal__membership-panel">
                  <div className="ipe-credits-purchase-modal__membership-panel-head">
                    <h3 className="ipe-credits-purchase-modal__membership-panel-title">选择会员档位与时长</h3>
                    {selectedTierRow ? (
                      <p className="ipe-credits-purchase-modal__membership-summary">
                        已选 {selectedTierRow.label}
                        {' · '}
                        {formatDurationShortLabel(selectedDurationMonths)}
                        {' · '}
                        {formatDurationPrice(Number(selectedTierRow.priceYuan), selectedDurationMonths)}
                      </p>
                    ) : null}
                  </div>

                  <div
                    className="ipe-credits-purchase-modal__pack-category-tabs"
                    role="tablist"
                    aria-label="会员类型"
                  >
                    {MEMBERSHIP_SCOPE_TABS.map((tab) => {
                      const Icon = tab.icon;
                      const isActive = packScope === tab.key;
                      return (
                        <button
                          key={tab.key}
                          type="button"
                          role="tab"
                          aria-selected={isActive}
                          className={`ipe-credits-purchase-modal__pack-category-tab${isActive ? ' ipe-credits-purchase-modal__pack-category-tab--active' : ''}`}
                          onClick={() => setPackScope(tab.key)}
                        >
                          <Icon size={13} strokeWidth={2} aria-hidden="true" />
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>

                  <p className="ipe-credits-purchase-modal__pack-panel-desc">
                    {packScope === 'team'
                      ? '团队会员适用于团队协作，主账号可创建团队并邀请成员共享额度'
                      : '个人会员适用于个人使用，权益与额度仅在个人工作区生效'}
                  </p>

                  <CardsCarousel
                    depKey={`membership-${packScope}-${selectedDurationMonths}-${purchasableMembershipTiers.length}`}
                  >
                    {purchasableMembershipTiers.map((row) => {
                      const isActive = selectedTier === row.tier;
                      const isCurrent = row.tier === user.planTier;
                      const monthlyPrice = Number(row.priceYuan) || 0;
                      const totalPrice = calcMembershipTotalPrice(monthlyPrice, selectedDurationMonths);
                      const originalTotal = calcMembershipOriginalTotal(monthlyPrice, selectedDurationMonths);
                      const showDiscount = originalTotal > totalPrice;
                      return (
                        <button
                          key={row.tier}
                          type="button"
                          className={`ipe-credits-purchase-modal__plan-card${isActive ? ' ipe-credits-purchase-modal__plan-card--active' : ''}${isCurrent ? ' ipe-credits-purchase-modal__plan-card--current' : ''}`}
                          onClick={() => setSelectedTier(row.tier)}
                        >
                          {isCurrent ? (
                            <span className="ipe-credits-purchase-modal__plan-badge">当前档位</span>
                          ) : null}
                          <div className="ipe-credits-purchase-modal__plan-name">{row.label}</div>
                          <div className="ipe-credits-purchase-modal__plan-price">
                            <small>¥</small>
                            {formatYuanAmount(totalPrice)}
                          </div>
                          {showDiscount ? (
                            <div className="ipe-credits-purchase-modal__plan-origin">
                              ¥{formatYuanAmount(originalTotal)}
                            </div>
                          ) : (
                            <div className="ipe-credits-purchase-modal__plan-sub">
                              ¥{formatYuanAmount(monthlyPrice)}/月
                            </div>
                          )}
                          <div className="ipe-credits-purchase-modal__plan-duration-tag">
                            {formatDurationShortLabel(selectedDurationMonths)}
                          </div>
                          <div className="ipe-credits-purchase-modal__plan-meta">
                            <span>{row.shopLimit} 店铺上限</span>
                            {row.bonusCredits ? (
                              <span>赠 {formatCredits(row.bonusCredits)} IPE 豆/月</span>
                            ) : null}
                            {row.storageLimitMb ? (
                              <span>{Math.round(Number(row.storageLimitMb) / 1024)}GB 空间</span>
                            ) : null}
                          </div>
                        </button>
                      );
                    })}
                  </CardsCarousel>

                  <div className="ipe-credits-purchase-modal__membership-panel-duration">
                    <DurationPicker
                      id="membership-duration-purchase"
                      label="订阅时长"
                      labelClassName="ipe-credits-purchase-modal__duration-label"
                      className="duration-picker--purchase-membership"
                      options={durationOptions}
                      value={selectedDurationMonths}
                      onChange={setSelectedDurationMonths}
                    />
                  </div>
                </div>
              ) : (
                <>
                  {availablePackGroups.length > 0 ? (
                    <div className="ipe-credits-purchase-modal__membership-panel ipe-credits-purchase-modal__pack-panel">
                      <div className="ipe-credits-purchase-modal__membership-panel-head">
                        <h3 className="ipe-credits-purchase-modal__membership-panel-title">
                          {activePackHasDuration ? '选择充值套餐与时长' : '选择充值套餐'}
                        </h3>
                        {selectedPack ? (
                          <p className="ipe-credits-purchase-modal__membership-summary">
                            已选 {selectedPack.label}
                            {activePackHasDuration ? (
                              <>
                                {' · '}
                                {formatDurationShortLabel(rechargeDurationMonths)}
                              </>
                            ) : null}
                            {selectedPackPriceLabel ? (
                              <>
                                {' · '}
                                {selectedPackPriceLabel}
                              </>
                            ) : null}
                          </p>
                        ) : null}
                      </div>

                      {availablePackGroups.length > 1 ? (
                        <div
                          className="ipe-credits-purchase-modal__pack-category-tabs"
                          role="tablist"
                          aria-label="充值类型"
                        >
                          {availablePackGroups.map((group) => (
                            <button
                              key={group}
                              type="button"
                              role="tab"
                              aria-selected={activePackGroup === group}
                              className={`ipe-credits-purchase-modal__pack-category-tab${activePackGroup === group ? ' ipe-credits-purchase-modal__pack-category-tab--active' : ''}`}
                              onClick={() => setActivePackGroup(group)}
                            >
                              {PACK_GROUP_LABELS[group]}
                            </button>
                          ))}
                        </div>
                      ) : null}

                      <div className="ipe-credits-purchase-modal__pack-panel-desc">
                        {activePackGroup === 'credit'
                          ? packScope === 'team'
                            ? '充值至团队 IPE 豆账户，团队成员在团队模式下共享消耗'
                            : '充值至个人 IPE 豆账户，仅在个人模式下消耗'
                          : activePackGroup === 'shop'
                            ? packScope === 'team'
                              ? '扩容团队店铺授权上限，与团队会员档位叠加生效'
                              : '扩容个人店铺授权上限，与个人会员档位叠加生效'
                            : packScope === 'team'
                              ? '扩容团队图片存储空间，与团队会员档位叠加生效'
                              : '扩容个人图片存储空间，与个人会员档位叠加生效'}
                      </div>

                      <CardsCarousel
                        depKey={`packs-${activePackGroup}-${packScope}-${rechargeDurationMonths}-${activePackList.length}`}
                      >
                        {activePackList.map((pack) => {
                          const key = `${activePackGroup}:${pack.id}`;
                          const isActive = selectedPackKey === key;
                          const hasDuration = packGroupHasDuration(activePackGroup);
                          const monthlyPrice = resolvePackUnitPrice(pack);
                          const totalPrice = hasDuration
                            ? calcPackTotalPrice(monthlyPrice, rechargeDurationMonths, activePackGroup === 'shop')
                            : monthlyPrice;
                          const originalTotal = hasDuration
                            ? calcMembershipOriginalTotal(monthlyPrice, rechargeDurationMonths)
                            : monthlyPrice;
                          const showDiscount = hasDuration && monthlyPrice > 0 && originalTotal > totalPrice;
                          const unitHint = activePackGroup === 'credit'
                            ? pack.pricePerBean
                            : activePackGroup === 'shop'
                              ? pack.pricePerShop
                              : null;
                          return (
                            <button
                              key={pack.id}
                              type="button"
                              className={`ipe-credits-purchase-modal__plan-card ipe-credits-purchase-modal__pack-card${isActive ? ' ipe-credits-purchase-modal__plan-card--active ipe-credits-purchase-modal__pack-card--active' : ''}`}
                              onClick={() => setSelectedPackKey(key)}
                            >
                              <div className="ipe-credits-purchase-modal__plan-name">{pack.label}</div>
                              <div className="ipe-credits-purchase-modal__pack-card-amount">
                                {formatPackAmount(pack, activePackGroup)}
                              </div>
                              {monthlyPrice > 0 ? (
                                <>
                                  <div className="ipe-credits-purchase-modal__plan-price">
                                    <small>¥</small>
                                    {formatYuanAmount(totalPrice)}
                                  </div>
                                  {showDiscount ? (
                                    <div className="ipe-credits-purchase-modal__plan-origin">
                                      ¥{formatYuanAmount(originalTotal)}
                                    </div>
                                  ) : unitHint ? (
                                    <div className="ipe-credits-purchase-modal__plan-sub">{unitHint}</div>
                                  ) : hasDuration ? (
                                    <div className="ipe-credits-purchase-modal__plan-sub">
                                      ¥{formatYuanAmount(monthlyPrice)}/月
                                    </div>
                                  ) : null}
                                </>
                              ) : (
                                <div className="ipe-credits-purchase-modal__plan-sub">联系商务报价</div>
                              )}
                              {hasDuration ? (
                                <div className="ipe-credits-purchase-modal__plan-duration-tag">
                                  {formatDurationShortLabel(rechargeDurationMonths)}
                                </div>
                              ) : null}
                            </button>
                          );
                        })}
                      </CardsCarousel>

                      {activePackHasDuration ? (
                        <div className="ipe-credits-purchase-modal__membership-panel-duration">
                          <DurationPicker
                            id="recharge-duration-purchase"
                            label="订阅时长"
                            labelClassName="ipe-credits-purchase-modal__duration-label"
                            className="duration-picker--purchase-membership"
                            options={durationOptions}
                            value={rechargeDurationMonths}
                            onChange={setRechargeDurationMonths}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}
            </div>

            <footer className="ipe-credits-purchase-modal__pay">
              {/* 冻结挂载点：扫码占位默认关闭，勿删 import / CSS。docs/payment-freeze/README.md */}
              {isScanPayPlaceholderVisible() ? (
                <RechargeScanPayPlaceholder surface="purchase" />
              ) : null}
              <div className="ipe-credits-purchase-modal__pay-body">
                <p className="ipe-credits-purchase-modal__pay-price">{payPriceLabel}</p>
                <div className="ipe-credits-purchase-modal__pay-actions">
                  <button
                    type="button"
                    className="ipe-credits-purchase-modal__add-cart-btn ipe-credits-purchase-modal__add-cart-btn--ghost"
                    onClick={handleAddToCart}
                  >
                    <ShoppingCart size={15} strokeWidth={2} aria-hidden="true" />
                    加入购物车
                  </button>
                  <button
                    type="button"
                    className="ipe-credits-purchase-modal__intent-btn"
                    onClick={handleOpenIntentCheckout}
                  >
                    提交意向
                  </button>
                </div>
                <p className="ipe-credits-purchase-modal__pay-method">{resolvePayMethodHint('purchase')}</p>
                <div className="ipe-credits-purchase-modal__pay-contact">
                  {recharge.phone ? (
                    <p>
                      <Phone size={14} strokeWidth={1.8} aria-hidden="true" />
                      客服电话：<a href={`tel:${recharge.phone}`}>{recharge.phone}</a>
                    </p>
                  ) : null}
                  {recharge.wechat ? (
                    <p>
                      <MessageCircle size={14} strokeWidth={1.8} aria-hidden="true" />
                      商务微信：{recharge.wechat}
                    </p>
                  ) : null}
                  <p>{contactHint}</p>
                </div>
                <p className="ipe-credits-purchase-modal__pay-foot">
                  开通即表示同意会员服务协议
                  {activeTab === 'packs' && !activePackHasDuration
                    ? ` · ${packScope === 'team' ? '团队' : '个人'}IPE 豆充值与店铺扩容为一次性购买，长期有效`
                    : ` · ${packScope === 'team' ? '团队' : '个人'}充值与扩容额度在有效期内可用`}
                </p>
              </div>
            </footer>
          </div>
        )}
    </div>
  );
}
